import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiGet, apiPost } from '@/lib/api';
import { Check, Crown, ChevronLeft, Loader2, Star, Zap } from 'lucide-react';
import { SetupStepper } from '@/components/study/SetupStepper';

const ORDER = ['growth', 'premium', 'elite'];
const ICONS = { growth: Star, premium: Crown, elite: Zap };
const ACCENT = {
  growth: 'border-blue-200 bg-blue-50/50',
  premium: 'border-amber-300 bg-amber-50/60 ring-2 ring-amber-300',
  elite: 'border-violet-200 bg-violet-50/50',
};

const rupees = n => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/** Step 4 of first-run setup. Nothing is charged — the student picks the plan
 *  that begins when their free trial ends. Annual is pre-selected. */
export function PlanChoiceDialog({ open, step = 4, totalSteps = 5, trialDays = 7, onDone, onBack }) {
  const [tiers, setTiers] = useState(null);
  const [cycle, setCycle] = useState('yearly');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!open) return;
    apiGet('/subscription/tiers').then(d => setTiers(d.tiers || {})).catch(() => setTiers({}));
  }, [open]);

  const rows = useMemo(() => ORDER
    .filter(k => tiers?.[k])
    .map(k => {
      const t = tiers[k];
      const plans = t.billing_plans || {};
      const plan = plans[cycle] || {};
      return { key: k, name: t.name || k, features: (t.features || []).slice(0, 6), plan, plans };
    }), [tiers, cycle]);

  const choose = async (tierKey) => {
    setBusy(tierKey);
    try {
      const res = await apiPost('/subscription/plan-intent', { tier: tierKey, cycle });
      toast.success(`${tiers[tierKey]?.name} saved — it starts after your free trial`);
      onDone?.(res);
    } catch (e) { toast.error(e?.message || 'Could not save that plan. Please try again.'); }
    setBusy('');
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-4xl rounded-3xl p-0 gap-0 flex flex-col max-h-[90dvh] overflow-hidden"
        hideClose
        onEscapeKeyDown={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
        onInteractOutside={e => e.preventDefault()}
        data-testid="plan-choice-dialog">
        <div className="flex-none px-5 sm:px-7 pt-6 pb-4 border-b border-slate-100 text-center relative">
          {onBack && (
            <button type="button" data-testid="plan-back-btn" onClick={onBack}
              className="absolute left-4 top-5 inline-flex items-center gap-1 text-[12px] font-heading text-slate-500 hover:text-slate-800">
              <ChevronLeft className="w-3.5 h-3.5" /> Back
            </button>
          )}
          <SetupStepper step={step} total={totalSteps}
            labels={['Mode Of Study', 'School & Class', 'Study theme', 'Choose your plan', 'Your syllabus']} />
          <DialogTitle className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
            Choose your plan
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 font-body mt-1">
            Everything is free for your first {trialDays} days. Pick the plan that takes over after that —
            you can change it any time before it starts.
          </DialogDescription>

          <div className="inline-flex items-center mt-4 p-1 rounded-full bg-slate-100" data-testid="plan-cycle-toggle">
            {[
              { k: 'yearly', label: 'Annual · 2 months free' },
              { k: 'monthly', label: 'Monthly' },
            ].map(opt => (
              <button key={opt.k} type="button" data-testid={`plan-cycle-${opt.k}`}
                onClick={() => setCycle(opt.k)}
                className={`px-4 py-1.5 rounded-full text-[12px] font-heading transition-all ${cycle === opt.k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-7 py-5">
          {!tiers ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
              {rows.map(row => {
                const Icon = ICONS[row.key] || Star;
                const popular = row.key === 'premium';
                const price = row.plan?.price;
                const available = !!row.plan?.enabled && Number(price) > 0;
                return (
                  <div key={row.key} data-testid={`plan-card-${row.key}`}
                    className={`relative rounded-2xl border p-4 flex flex-col ${ACCENT[row.key] || 'border-slate-200'} ${popular ? 'md:-mt-2 md:mb-2 shadow-lg' : ''}`}>
                    {popular && (
                      <span data-testid="plan-most-popular-badge"
                        className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-heading uppercase tracking-wider shadow">
                        Most Popular
                      </span>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Icon className="w-4 h-4 text-slate-700" />
                      <p className="font-heading text-base font-bold text-slate-900">{row.name}</p>
                    </div>
                    <p className="mt-2 font-heading text-2xl font-extrabold text-slate-900" data-testid={`plan-price-${row.key}`}>
                      {available ? rupees(price) : '—'}
                      <span className="text-[11px] font-body text-slate-500 ml-1">
                        {cycle === 'yearly' ? '/year' : '/month'}
                      </span>
                    </p>
                    {available && cycle === 'yearly' && row.plans?.monthly?.price ? (
                      <p className="text-[11px] font-body text-slate-500">
                        {rupees(Math.round(price / 12))}/month, billed once a year
                      </p>
                    ) : null}
                    <ul className="mt-3 space-y-1.5 flex-1">
                      {row.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[12px] font-body text-slate-600">
                          <Check className="w-3.5 h-3.5 text-emerald-500 mt-[2px] flex-shrink-0" />{f}
                        </li>
                      ))}
                    </ul>
                    <Button className="mt-4 w-full rounded-full font-heading text-xs"
                      data-testid={`plan-select-${row.key}`}
                      disabled={!available || !!busy}
                      onClick={() => choose(row.key)}>
                      {busy === row.key && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                      {available ? `Start with ${row.name}` : 'Not available'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-[11px] text-slate-400 font-body text-center mt-5">
            No card needed today. We’ll remind you before your free {trialDays} days end.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PlanChoiceDialog;
